/**
 * timerService.ts
 * -------------------------------------------------------------
 * 集中计时器（最小堆实现）
 *
 * - 只维护 **一个** setTimeout
 * - 所有任务按 nextTime 存入最小堆（binary-heap）
 * - 插入 / 更新 / 删除 O(log N)，取最近任务 O(1)
 * - 自动处理浏览器挂起漂移、Page Visibility 降频
 * -------------------------------------------------------------
 */

import dayjs, { Dayjs } from 'dayjs'

type TimerTask = {
  id: string
  nextTime: Dayjs // 下一次触发点（服务器时区 or 本地均可）
  callback: () => void // 到时回调；回调里务必调用 updateTask/resetTask
  index: number // 任务在堆中的当前下标（维护 O(log N) 操作）
}

/* ------------------------------------------------------------------ */
/* 私有数据结构：最小堆 + 索引表                                        */
/* ------------------------------------------------------------------ */
const taskHeap: TimerTask[] = [] // index 0 = 最早任务
const taskMap = new Map<string, TimerTask>() // O(1) 查找任务 by id

/* 交换堆中两个元素并同步下标 */
function swap(i: number, j: number) {
  ;[taskHeap[j], taskHeap[i]] = [taskHeap[i], taskHeap[j]]
  taskHeap[i].index = i
  taskHeap[j].index = j
}

/* 上浮：保持最小堆性质 */
function siftUp(i: number) {
  while (i > 0) {
    const parent = (i - 1) >> 1
    if (!taskHeap[i].nextTime.isBefore(taskHeap[parent].nextTime)) break
    swap(i, parent)
    i = parent
  }
}

/* 下沉：保持最小堆性质 */
function siftDown(i: number) {
  const n = taskHeap.length
  while (true) {
    let smallest = i
    const left = 2 * i + 1
    const right = 2 * i + 2
    if (left < n && taskHeap[left].nextTime.isBefore(taskHeap[smallest].nextTime)) smallest = left
    if (right < n && taskHeap[right].nextTime.isBefore(taskHeap[smallest].nextTime))
      smallest = right
    if (smallest === i) break
    swap(i, smallest)
    i = smallest
  }
}

/* ------------------------------------------------------------------ */
/* 计时器核心逻辑                                                      */
/* ------------------------------------------------------------------ */
let activeTimerId: ReturnType<typeof setTimeout> | null = null // 当前 setTimeout 句柄
let lastTickTime = dayjs() // 上一次 run() 触发时刻

/** 获取堆顶（最早任务），为空返回 null */
function getEarliestTask(): TimerTask | null {
  return taskHeap.length ? taskHeap[0] : null
}

/** 根据当前堆顶重新设置 setTimeout */
function rescheduleTimer(): void {
  if (activeTimerId) clearTimeout(activeTimerId)
  const earliest = getEarliestTask()
  if (!earliest) return // 无任务
  const delayMs = Math.max(earliest.nextTime.diff(dayjs()), 0)
  activeTimerId = setTimeout(processDueTasks, delayMs)
}

/** 到期触发：执行所有应到期任务，然后重排下一次定时 */
function processDueTasks(): void {
  const now = dayjs()
  const driftMs = now.diff(lastTickTime)
  lastTickTime = now
  const forceRun = driftMs > 60_000 // 浏览器挂起 >1min 时强制触发全部任务

  /* 依次弹出所有到期任务并执行 */
  while (taskHeap.length) {
    const top = taskHeap[0]
    if (!forceRun && top.nextTime.isAfter(now)) break // 无更多到期
    removeTask(top.id, /*reSchedule*/ false) // 堆/表删除
    top.callback() // 业务回调
  }

  rescheduleTimer() // 设置下一次 setTimeout
}

/**
 * 新增/覆盖任务
 * @param id        任务唯一标识
 * @param nextTime  下一次触发 Dayjs 实例
 * @param callback  触发时回调
 */
export function registerTask(id: string, nextTime: Dayjs, callback: () => void): void {
  if (taskMap.has(id)) removeTask(id, false) // 覆盖旧任务
  const task: TimerTask = { id, nextTime, callback, index: taskHeap.length }
  taskHeap.push(task)
  taskMap.set(id, task)
  siftUp(task.index)
  rescheduleTimer()
}

/**
 * 更新已存在任务的 nextTime
 * 若 id 不存在则作为空回调任务插入（容错）
 */
export function updateTask(id: string, nextTime: Dayjs): void {
  const task = taskMap.get(id)
  if (!task) {
    registerTask(id, nextTime, () => {})
    return
  }
  task.nextTime = nextTime
  siftUp(task.index)
  siftDown(task.index)
  rescheduleTimer()
}

/**
 * 移除任务
 * @param reSchedule 默认 true，内部循环时可传 false 减少重复调度
 */
export function removeTask(id: string, reSchedule = true): void {
  const task = taskMap.get(id)
  if (!task) return
  const idx = task.index
  const last = taskHeap.pop()!
  taskMap.delete(id)

  if (idx < taskHeap.length) {
    taskHeap[idx] = last
    last.index = idx
    siftUp(idx)
    siftDown(idx)
  }
  if (reSchedule) rescheduleTimer()
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') rescheduleTimer()
  })
}
